import { ReportScreen } from 'app/area/company-report/components/report-screen';
import { 
  useCompanyReportFavouriteEffect,
  useIsReportDisabled,
  useScheduleDefinitionCallback,
  useKeyboardHandlers, 
  useLoadScheduleEffect, 
  useSaveSendReportCallbacks,
  useScheduleDefinition
} from 'app/area/company-report/hooks';
import { selectValidateReportFilters } from 'app/area/company-report/selectors';
import { Metadata } from 'app/area/company-report/types-store';
import { useFavouriteDefinitions, 
  useSavedReportState, 
  useSearchRequiredUsers, 
  useValidateFavouriteFilters 
} from 'app/area/favourite/hooks';
import { routeConstants } from 'app/routes/route-constants';
import { SharedState } from 'app/types-shared';
import * as React from 'react';
import { useCallback } from 'react';
import { KeyMap } from 'react-hotkeys';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';
import { ValidationProps } from 'ui';
import { keyCodeTypes } from 'ui/common/key-code-types';

const keyMap: KeyMap = {
  onEnter: [keyCodeTypes.Enter]
};

interface ReportViewFavouriteProps {
  companyReportId: number;
  sharedState: SharedState;
  showImportDocumentModal: boolean;
  handleAddTemplate: () => void;
  handleUploadTemplate: (file: File) => void;
  handleDownloadTemplate: () => void;
  onClose: () => void;
  translation: any;
  onChangeEmail: (value: string, validation?: ValidationProps) => void;
  metadata: Metadata;
  emails: string;
  fetchingReport: boolean;
  fetchingMetadata: boolean;
  emailsValidation?: ValidationProps;
  editSavedFavourite: boolean;
  favouriteId: number;
}

export function ReportViewFavourite({
  companyReportId,
  sharedState,
  showImportDocumentModal,
  handleAddTemplate,
  handleUploadTemplate,
  handleDownloadTemplate,
  onClose,
  translation,
  onChangeEmail,
  metadata,
  emails,
  fetchingReport,
  fetchingMetadata,
  emailsValidation,
  editSavedFavourite,
  favouriteId
}: ReportViewFavouriteProps) {

  useCompanyReportFavouriteEffect(companyReportId, favouriteId);
  useLoadScheduleEffect(favouriteId);
  
  const success = useCallback(() => <Link
    style={{ color: '#fff' }}
    to={routeConstants.myDownloadsView}>
    {translation.report_success}
  </Link>, [translation]);

  const handlers = useKeyboardHandlers(companyReportId, success);

  const {
    onSendReport,
    onSaveReport
  } = useSaveSendReportCallbacks(success);

  const {
    onDeleteScheduleDefinition,
    onUpdateScheduleDefinition,
    onCreateScheduleDefinition,
    onUpdateScheduleDefinitionOnSubmit
  } = useScheduleDefinitionCallback(favouriteId);

  const {
    definition
  } = useFavouriteDefinitions();

  const {
    scheduleDefinition,
    fetchingScheduleDefinition,
    scheduleDefinitionChanged,
    scheduleFailedConstraints
  } = useScheduleDefinition();
  
  const {
    createReport,
    updateExistingReport,
    onCloseReport,
    onCreateReport,
    onUpdateReport
  } = useSavedReportState(editSavedFavourite, true);

  const searchRequiredUsers = useSearchRequiredUsers();
  const favouriteOwnerId = definition?.validState?.owner?.v;
  const showUpdate = sharedState?.id === favouriteOwnerId;

  const validateFavouriteFilters = useValidateFavouriteFilters();
  const validateReportFilters = useSelector(selectValidateReportFilters);
  const validateFilters = validateFavouriteFilters || validateReportFilters;
  const reportIsDisabled = useIsReportDisabled(definition);
  
  return (
    <ReportScreen
      keyMap={keyMap}
      handlers={handlers}
      sendReport={onSendReport}
      saveReport={onSaveReport}
      translation={translation}
      sharedState={sharedState}
      companyReportId={companyReportId}
      showImportDocumentModal={showImportDocumentModal}
      handleAddTemplate={handleAddTemplate}
      handleUploadTemplate={handleUploadTemplate}
      handleDownloadTemplate={handleDownloadTemplate}
      onClose={onClose}
      onChangeEmail={onChangeEmail}
      metadata={metadata}
      emails={emails}
      fetchingReport={fetchingReport}
      fetchingMetadata={fetchingMetadata}
      emailsValidation={emailsValidation}
      validateFilters={validateFilters}
      isCreateReportOpen={createReport}
      definition={definition}
      updateExistingReport={updateExistingReport}
      onCloseReport={onCloseReport}
      onCreateReport={onCreateReport}
      onUpdateReport={onUpdateReport}
      showUpdate={showUpdate}
      searchRequiredUsers={searchRequiredUsers}
      deleteScheduleDefinition={onDeleteScheduleDefinition}
      scheduleDefinition={scheduleDefinition}
      updateScheduleDefinition={onUpdateScheduleDefinition}
      fetchingScheduleDefinition={fetchingScheduleDefinition}
      scheduleDefinitionChanged={scheduleDefinitionChanged}
      reportIsDisabled={reportIsDisabled}
      createScheduleDefinition={onCreateScheduleDefinition}
      scheduleFailedConstraints={scheduleFailedConstraints}
      updateScheduleDefinitionOnSubmit={onUpdateScheduleDefinitionOnSubmit}
    />
  );
}
